import { useGetProviderSearchPatients } from "apollo/hooks/queries";
import {
  PROVIDER_SEARCH_BAVARIA,
  PROVIDER_SEARCH_VERSION_MAP,
  REDIRECT_REASON_PARAM,
} from "core/consts";
import { TOKEN_STATUS } from "core/model/accounts";
import {
  getProviderSearchFromPathname,
  PROVIDER_SEARCH_LOCAL_STORAGE_KEY,
} from "core/model/providerSearch";
import {
  PROVIDER_SEARCH_CONFIG,
  ProviderSearchConfig,
} from "core/model/providerSearch/config";
import { isProd } from "core/model/utils/featureFlags";
import { ActiveProviderSearch, ProviderSearchPatients } from "core/types";
import {
  PROVIDER_SEARCH_LOGIN_MODAL,
  ProviderSearchLoginModalType,
} from "dsl/ecosystems/ProviderSearchLoginModal/shared";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
  useIsLoggedOnProviderSearchApp,
  useLoggedCareseeker,
  useLoggedInAccount,
  useOnLogout,
} from "reduxentities/hooks";
import { selectLoggedCareseeker } from "reduxentities/selectors";

export type ProviderSearchContext = {
  config: ProviderSearchConfig;
  isLoggedIn: boolean;
  loginModal: ProviderSearchLoginModalType;
  patients: ProviderSearchPatients;
  providerSearch: ActiveProviderSearch;
  setLoginModal: React.Dispatch<
    React.SetStateAction<ProviderSearchLoginModalType>
  >;
};

const defaultProviderSearch = PROVIDER_SEARCH_BAVARIA;

const defaultProviderSearchContext: ProviderSearchContext = {
  config: PROVIDER_SEARCH_CONFIG[defaultProviderSearch],
  isLoggedIn: false,
  loginModal: null,
  patients: { active: [], archived: [] },
  providerSearch: defaultProviderSearch,
  setLoginModal: () => {},
};

const ProviderSearchContext = createContext<ProviderSearchContext>(
  defaultProviderSearchContext,
);

export function useProviderSearchContext() {
  return useContext<ProviderSearchContext>(ProviderSearchContext);
}

const useLoggedInProviderSearch = (): ProviderSearchContext["isLoggedIn"] => {
  const dispatch = useAppDispatch();
  const logged = useIsLoggedOnProviderSearchApp();
  const onLogout = useOnLogout();
  const account = useLoggedInAccount();

  useEffect(() => {
    if (logged !== "ok") {
      if (account?.id) {
        // temporary log to confirm why users are getting logged out
        if (logged !== "noToken") {
          console.error("provider search user logged out", { logged });
        }
        onLogout({ tokenExpired: logged === TOKEN_STATUS.EXPIRED });
      }
    }
  }, [logged, dispatch]);

  return logged === "ok";
};

const useGetProviderSearchConfig = (): Pick<
  ProviderSearchContext,
  "config" | "providerSearch"
> => {
  const navigate = useNavigate();
  const stateSlug = getProviderSearchFromPathname();

  useEffect(() => {
    if (stateSlug && !!PROVIDER_SEARCH_VERSION_MAP[stateSlug]) {
      localStorage.setItem(PROVIDER_SEARCH_LOCAL_STORAGE_KEY, stateSlug);
    }
  }, [stateSlug]);

  const resolvedProviderSearch: ActiveProviderSearch = useMemo(() => {
    const fromMap = stateSlug ? PROVIDER_SEARCH_VERSION_MAP[stateSlug] : null;

    if (!fromMap && stateSlug) {
      // if no provider search found from params - it is incorrectly entered into the URL; redirect user to listing
      // this should only happen in test env's as provider search implementation can be derived from the domain in getProviderSearchFromPathname
      if (!isProd) {
        console.warn(
          `Unknown provider search state slug "${stateSlug}" – falling back to "${defaultProviderSearch}".`,
        );
      }
    }

    return fromMap ?? defaultProviderSearch;
  }, [stateSlug]);

  // If a bad state was provided, normalize the URL.
  useEffect(() => {
    if (!stateSlug) return;
    if (!PROVIDER_SEARCH_VERSION_MAP[stateSlug]) {
      console.error(
        `Could not determine provider search from slug "${stateSlug}". Redirecting to fallback listing "${defaultProviderSearch}".`,
      );
      navigate(`providersearch/${defaultProviderSearch}/listing`, {
        replace: true,
      });
    }
  }, [stateSlug, navigate]);

  // should only happen in test env's as provider search implementation can be derived from the domain in in production with getProviderSearchFromPathname
  useEffect(() => {
    if (!isProd && stateSlug) {
      const mapped = PROVIDER_SEARCH_VERSION_MAP[stateSlug];
      if (mapped && mapped !== resolvedProviderSearch) {
        console.info(
          `Resetting provider search from "${resolvedProviderSearch}" to "${mapped}".`,
        );
      }
    }
  }, [stateSlug, resolvedProviderSearch]);

  const config = useMemo(
    () => PROVIDER_SEARCH_CONFIG[resolvedProviderSearch],
    [resolvedProviderSearch],
  );

  return {
    config,
    providerSearch: resolvedProviderSearch,
  };
};

const useGetProviderSearchContext = (): ProviderSearchContext => {
  const { config, providerSearch } = useGetProviderSearchConfig();
  const [search] = useSearchParams();
  const isLoggedIn = useLoggedInProviderSearch();
  const loggedCareseeker = useAppSelector(selectLoggedCareseeker);
  const careseeker = useLoggedCareseeker({
    fetchPolicy: isLoggedIn ? "cache-first" : undefined,
    skip: !loggedCareseeker?.id,
  });
  const [, patients = { active: [], archived: [] }] =
    useGetProviderSearchPatients({
      skip: !careseeker?.id,
    });
  const [loginModal, setLoginModal] =
    useState<ProviderSearchLoginModalType>(null);

  useEffect(() => {
    if (search.has(REDIRECT_REASON_PARAM)) {
      setLoginModal(PROVIDER_SEARCH_LOGIN_MODAL);
    }
  }, [search]);

  return {
    config,
    isLoggedIn,
    loginModal,
    patients: {
      active: patients.active ?? [],
      archived: patients.archived ?? [],
    },
    providerSearch,
    setLoginModal,
  };
};

export const ProviderSearchProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const context = useGetProviderSearchContext();

  if (!context.providerSearch || !context.config) return null;

  return (
    <ProviderSearchContext.Provider value={context}>
      {children}
    </ProviderSearchContext.Provider>
  );
};

export const TestProviderSearchProvider = ({
  children,
  providerSearchContext,
}: {
  children: React.ReactNode;
  providerSearchContext?: Partial<ProviderSearchContext>;
}) => {
  return (
    <ProviderSearchContext.Provider
      value={{ ...defaultProviderSearchContext, ...providerSearchContext }}
    >
      {children}
    </ProviderSearchContext.Provider>
  );
};
